import { getDefaultLoggingSettings, ILoggingSettings, LoggingLevel, LoggingSection } from "@multiweb/logging";
import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { AppConfigurationBase } from "./AppConfigurationBase";
import { IAppConfiguration } from "./IAppConfiguration";
import { ExperimentName, IDevelopmentSettings, IInitParams } from "./IInitParams";

export class AppConfiguration extends AppConfigurationBase implements IAppConfiguration {
    constructor(initParamsRaw: Partial<IInitParams>) {
        if (!initParamsRaw) {
            super(AppConfiguration._createDefault());
        } else {
            super(AppConfiguration._validateAndAddDefaults(initParamsRaw));
        }

        if (this._initParams.developmentMode && !(location.hostname === "localhost" || location.hostname === "127.0.0.1")) {
            const logger = createDefaultSectionLoggerFactory(this.logging).create(LoggingSection.configuration);
            logger.warn("Development mode enabled. It cannot be enabled in production environment!");
        }
    }

    public override get defaultWhisperer(): string {
        if (StringComparisonTypes.isStartsWith(this._initParams.defaultWhisperer))
            return StringComparisonTypes.startsWith;
        else
            return StringComparisonTypes.contains;
    }

    public override get defaultFilter(): string {
        if (StringComparisonTypes.isContains(this._initParams.defaultFilter))
            return StringComparisonTypes.contains;
        else
            return StringComparisonTypes.startsWith;
    }

    private static _createDefault(): IInitParams {
        return {
            appName: "",
            clientCode: "MW",
            reportType: "pdf",
            formUI: "standard",
            developmentMode: false,
            logging: getDefaultLoggingSettings(false),
            heartBeatInterval: 0,
            heartBeatTimeout: 0,
            userActionLoggerInterval: 0,
            defaultWhisperer: StringComparisonTypes.contains,
            defaultFilter: StringComparisonTypes.startsWith,
            openedFormsHistoryCount: 5,
            forceIdsInSelectAllConditionThreshold: 500,
            showMainMenuExpanded: false,
            showMainMenuCaptions: false,
            legacyActiveRoles: false,
            defaultShowSettings: false,
            webServiceUrl: "..",
            preloadFormCount: 10,
        };
    }

    private static _createDefaultDevelopmentSettings(): IDevelopmentSettings {
        return {};
    }

    private static _getRawLoggingSettings(initParamsRaw: Partial<IInitParams>): ILoggingSettings {
        const defaultSettings = getDefaultLoggingSettings(!!initParamsRaw.developmentMode);
        if (initParamsRaw.logging) {
            return {
                sections: initParamsRaw.logging.sections ?? defaultSettings.sections,
                defaultLevel: initParamsRaw.logging.defaultLevel ?? defaultSettings.defaultLevel,
            };
        } else {
            return defaultSettings;
        }
    }

    private static _validateAndAddDefaults(initParamsRaw: Partial<IInitParams>): IInitParams {
        if (!initParamsRaw)
            throw new Error("Argument 'initParams' cannot be null");

        const initParams = { ...initParamsRaw };

        const logger = createDefaultSectionLoggerFactory(AppConfiguration._getRawLoggingSettings(initParamsRaw)).create(LoggingSection.configuration);

        const validate = <T extends object, K extends keyof T>(options: T, defaults: T, property: K, predicate: (propertyValue: T[K]) => boolean, propertyTypeName: string, allowNull: boolean = false) => {
            if (options[property] == null) {
                if (!allowNull)
                    logger.error(`Undefined property '${String(property)}'. Default value '${defaults[property]}' used instead.`); //`
                options[property] = defaults[property];
            } else if (!predicate(options[property])) {
                logger.error(`Parameter '${String(property)}' is not ${propertyTypeName}. Default value '${defaults[property]}' used instead.`); //`
                options[property] = defaults[property];
            }
        };

        const validateString = <T extends object, K extends keyof T>(options: T, defaults: T, property: K, allowNull: boolean = false) => {
            validate(options, defaults, property, (val) => typeof val === "string", "string", allowNull);
        };
        const validateArray = <T extends object, K extends keyof T>(options: T, defaults: T, property: K, allowNull: boolean = false) => {
            validate(options, defaults, property, (val) => Array.isArray(val), "array", allowNull);
        };
        const validateBoolean = <T extends object, K extends keyof T>(options: T, defaults: T, property: K, allowNull: boolean = false) => {
            validate(options, defaults, property, (val) => typeof val === "boolean", "boolean", allowNull);
        };

        /**
         * Usnadňuje validaci volitelné položky (`key/value`) objektu.
         * - Pokud je klíč platný, pak zvaliduje hodnotu.
         * - Pokud klíč platný není, pak zaloguje chybu a položku z rodiče odstraní.
         *
         * @param key Klíč, jehož se validace týká. (Vlastní validace se tu neprovádí. Slouží pro zalogování chyby a pro odebrání z rodiče.)
         * @param isKeyValid _True_, pokud je `key` platný.
         * @param parent Rodičovský objekt pro `key`. (Aneb `parent[key]` je předmětem validace.)
         * @param parentName Jméno rodičovského objektu. Bude součástí error-message v případě nevalidního klíče.
         * @param valueValidationCallback Provede validaci hodnoty (`parent[key]`), pokud je klíč validní.
         */
        const validateOrRemoveIfKeyInvalid = (key: string, isKeyValid: boolean, parent: {[key: string]: any}, parentName: string, valueValidationCallback: () => void) => {
            if (isKeyValid) {
                valueValidationCallback();
            } else {
                logger.error(`Key '${String(key)}' is not valid for ${parentName}. The key will be REMOVED!`);
                delete parent[key];
            }
        };

        /*const validateNumber = <T extends object, K extends keyof T>(options: T, defaults: T, property: K, allowNull: boolean = false) => {
            validate(options, defaults, property, (val) => typeof val === "number", "number", allowNull);
        };*/
        const validateNonNegativeNumber = <T extends object, K extends keyof T>(options: T, defaults: T, property: K, allowNull: boolean = false) => {
            validate(options, defaults, property, (val) => typeof val === "number" && val >= 0, "positive number or zero", allowNull);
        };

        const validateLoggingLevel = <T extends object, K extends keyof T>(options: T, defaults: T, property: K) => {
            const predicate = (val: any) => ((typeof val === "number") || Object.keys(LoggingLevel).includes(val));
            validate(options, defaults, property, predicate, "LoggingLevelEnumParam", true);

            // Převod ze řetězce na číslo (např. "info" -> 3000). String totiž není platná hodnota pro LoggingLevelType.
            // String slouží pouze pro čitelnější zápis v initParams.json či config.js.
            if (typeof options[property] !== "number") {
                options[property] = (LoggingLevel as any)[options[property]];
            }
        };

        /**
         * Zvaliduje komplet nastavení {@link IInitParams.logging}.
         */
        const validateLogging = (initParams: Partial<IInitParams>, defaults: IInitParams) => {
            if (initParams.developmentMode) {
                defaults.logging = getDefaultLoggingSettings(true);
            }

            validate(initParams, defaults, "logging", (val) => typeof val === "object", "ILoggingSettings", true);
            const loggingSettings = initParams.logging!; // validate výše zajistí existenci
            validate(loggingSettings, defaults.logging, "sections", (val) => typeof val === "object", "object (section)", true);

            Object.entries(loggingSettings.sections).forEach(([key, _value]) => {
                const isKeyValid = (typeof key === "string");
                validateOrRemoveIfKeyInvalid(key, isKeyValid, loggingSettings.sections, "logging.sections", () => {
                    validateLoggingLevel(loggingSettings.sections, defaults.logging.sections, key);
                });
            });

            validateLoggingLevel(loggingSettings, defaults.logging, "defaultLevel");
        };

        const defaults = AppConfiguration._createDefault();
        validateString(initParams, defaults, "appName");
        validateString(initParams, defaults, "clientCode", true);
        validateString(initParams, defaults, "reportType", true);
        validateArray(initParams, defaults, "menuItems", true);
        validateString(initParams, defaults, "formUI", true);
        validateNonNegativeNumber(initParams, defaults, "heartBeatInterval", true);
        validateNonNegativeNumber(initParams, defaults, "heartBeatTimeout", true);
        validateNonNegativeNumber(initParams, defaults, "userActionLoggerInterval", true);
        validateNonNegativeNumber(initParams, defaults, "openedFormsHistoryCount", true);
        validateNonNegativeNumber(initParams, defaults, "forceIdsInSelectAllConditionThreshold", true);
        validateArray(initParams, defaults, "dependencies", true);
        validateString(initParams, defaults, "webServiceUrl", true);
        validateBoolean(initParams, defaults, "showMainMenuExpanded", true);
        validateBoolean(initParams, defaults, "showMainMenuCaptions", true);
        validateBoolean(initParams, defaults, "legacyActiveRoles", true);
        validateBoolean(initParams, defaults, "defaultShowSettings", true);

        validate(initParams, defaults, "developmentMode", (val) => typeof val === "boolean" || typeof val === "object", "boolean | IDevelopmentSettings", true);

        if (initParams.developmentMode === true)
            initParams.developmentMode = AppConfiguration._createDefaultDevelopmentSettings();
        else if (typeof initParams.developmentMode === "object") {
            const defaultsDev = AppConfiguration._createDefaultDevelopmentSettings();
            validateString(initParams.developmentMode, defaultsDev, "userName", true);
            validateString(initParams.developmentMode, defaultsDev, "userPassword", true);
            validateString(initParams.developmentMode, defaultsDev, "webServiceVersion", true);
            validateString(initParams.developmentMode, defaultsDev, "debugLocallyEvaluableConditions", true);
        }

        validateLogging(initParams, defaults);

        if (initParams.formUI?.toLowerCase() === "default")
            initParams.formUI = "standard";

        return initParams as IInitParams;
    }

    public getDevelopmentOption<K extends keyof IDevelopmentSettings>(option: K): IDevelopmentSettings[K] | null {
        const settings = this._initParams?.developmentMode;
        if (!settings)
            return null;

        if (typeof settings !== "object")
            throw new Error("Invalid value for 'initParams.developmentMode'");

        return settings[option];
    }

    public isExperimentEnabled(experimentName: ExperimentName): boolean {
        const settings = this._initParams?.experiments;
        if (!settings)
            return false;

        if (settings === true)
            return true;

        if (!Array.isArray(settings))
            throw new Error("Invalid value for 'initParams.experiments'");

        return settings.includes(experimentName); // case sensitive, schválně
    }
}

/**
 * Typy porovnávání řetězců v našeptávači LOV a ve filtrech.
 */
export class StringComparisonTypes {
    /**
     * Konfigurační hodnota pro typ porovnání "Začíná", jak je uvedena v initParams
     */
    public static readonly startsWith = "startsWith";
    /**
     * Konfigurační hodnota pro typ porovnání "Obsahuje", jak je uvedena v initParams
     */
    public static readonly contains = "contains";

    /**
     * Vrací, zda hodnota uvedená v initParams je typu "Začíná" (= "startsWith")
     * @param paramValue Hodnota z initParams
     */
    public static isStartsWith(paramValue: string): boolean {
        return (paramValue === StringComparisonTypes.startsWith);
    }

    /**
     * Vrací, zda hodnota uvedená v initParams je typu "Obsahuje" (= "contains")
     * @param paramValue Hodnota z initParams
     */
    public static isContains(paramValue: string): boolean {
        return (paramValue === StringComparisonTypes.contains);
    }
}
